// Palette de couleurs d'un document : nom lisible d'une couleur, et écriture
// de ses champs dans la palette.
//
// Une couleur est `{ label, style }`. Le `label` est son IDENTIFIANT (les box
// s'y réfèrent, le dédoublonnage se fait sur label+style) et vaut `color<uuid>`
// pour tout ce qui est ajouté depuis la modale : illisible. Le nom saisi par
// l'utilisateur vit donc dans un champ `name` à part, et le label ne bouge
// jamais.
//
// Sans nom saisi, on affiche le code hexadécimal : deux bleus voisins se
// distinguent déjà à l'œil nu, sans que personne ait eu à nommer quoi que ce
// soit. Les couleurs livrées avec le document gardent leur nom courant.

/** Couleurs présentes dans toute palette, désignées par leur label. */
export const DEFAULT_COLOR_NAMES = {
  white: 'Blanc',
  black: 'Noir',
  transparent: 'Transparent',
  yellow: 'Jaune',
};

const CHANNELS = 'rgba\\(\\s*([\\d.]+)\\s*,\\s*([\\d.]+)\\s*,\\s*([\\d.]+)\\s*(?:,\\s*([\\d.]+)\\s*)?\\)';

const toHex = (channel) => Math.round(Number(channel)).toString(16).padStart(2, '0').toUpperCase();

/**
 * Code hexadécimal d'un style CSS, suivi de son opacité quand elle n'est pas
 * pleine : deux couleurs de même teinte mais d'opacités différentes ne doivent
 * pas s'afficher pareil.
 */
export const colorHexLabel = (style) => {
  if (!style || typeof style !== 'string') return '';
  const value = style.trim();
  if (value.toLowerCase() === 'transparent') return DEFAULT_COLOR_NAMES.transparent;

  let r;
  let g;
  let b;
  let alpha = 1;
  const rgba = value.match(new RegExp(`^${CHANNELS}$`, 'i'));
  if (rgba) {
    [, r, g, b] = rgba;
    if (rgba[4] !== undefined) alpha = Number(rgba[4]);
  } else {
    const hex = value.match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i);
    if (!hex) return value;
    const digits = hex[1].length === 3
      ? hex[1].split('').map((d) => d + d).join('')
      : hex[1];
    r = parseInt(digits.slice(0, 2), 16);
    g = parseInt(digits.slice(2, 4), 16);
    b = parseInt(digits.slice(4, 6), 16);
    if (digits.length === 8) alpha = parseInt(digits.slice(6, 8), 16) / 255;
  }

  if (!Number.isFinite(alpha)) alpha = 1;
  if (alpha <= 0) return DEFAULT_COLOR_NAMES.transparent;
  const hexCode = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  return alpha >= 1 ? hexCode : `${hexCode} (${Math.round(alpha * 100)} %)`;
};

/** Nom saisi, sinon nom d'usage de la couleur livrée, sinon son code. */
export const colorDisplayName = (color) => {
  if (!color) return '';
  const name = typeof color.name === 'string' ? color.name.trim() : '';
  if (name) return name;
  return DEFAULT_COLOR_NAMES[color.label] || colorHexLabel(color.style);
};

/**
 * Écrit (ou efface) le nom de la couleur portant ce label, dans le tableau
 * donné — appelé sur un draft immer, donc en mutation. Sans correspondance, ne
 * fait rien : une couleur venue d'une section liée ne vit pas dans la palette
 * du document, la renommer ici n'aurait aucun sens.
 */
export const setColorName = (colors, label, name) => {
  const color = (colors || []).find((c) => c.label === label);
  if (!color) return;
  const trimmed = (name || '').trim();
  if (trimmed) color.name = trimmed;
  else delete color.name;
};

/**
 * Change la couleur elle-même, repérée par son label — même règle que
 * setColorName. Une couleur venue d'une section liée n'est pas dans la palette
 * du document : on ne touche à rien plutôt que d'écrire à côté.
 */
export const setColorStyle = (colors, label, style) => {
  const color = (colors || []).find((c) => c.label === label);
  if (!color) return;
  color.style = style;
};
